// =============================================================================
// DiaryPhotosField.tsx — T-258. Multi-photo capture/staging for Site Diary.
//
// Stages files in component state — parent (NewDiaryEntryScreen) calls
// uploadDiaryPhoto() for each staged file AFTER the parent entry has been
// inserted (so diary_id is known).
//
// Features:
//   - Multi-select file input (camera or gallery)
//   - Up to MAX_PHOTOS per entry (10, per T-258 alignment)
//   - Per-photo caption (optional)
//   - Reorder via up/down arrows (drag-drop is a Phase-2 nice-to-have)
//   - Live preview thumbnails (object URLs)
//   - Total size display so user sees if they're approaching email-friendly limits
// =============================================================================

import { useState, useRef, useEffect } from "react";
import { ChevronUp, ChevronDown, X, Camera, ImagePlus, AlertTriangle, Stamp } from "lucide-react";

const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;  // 10MB per photo
const ACCEPTED_MIMES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

// T-329 — optional photo category so the report can group photos.
export const PHOTO_KINDS = [
  { value: "general", label: "General" },
  { value: "setup",   label: "Site setup" },
  { value: "works",   label: "Works done" },
  { value: "delay",   label: "Delay / issue" },
  { value: "plant",   label: "Plant" },
];

export interface StagedPhoto {
  /** Stable temp id used as React key + when removing. */
  tempId:  string;
  file:    File;
  caption: string;
  kind:    string;   // T-329 one of PHOTO_KINDS values; defaults to 'general'
  /** T-366 — burn the date/GPS/address watermark on upload. Default true.
   *  A single per-entry toggle sets this on every staged photo. */
  watermark: boolean;
}

interface Props {
  /** Current staged photos — controlled by parent. */
  photos:   StagedPhoto[];
  /** Update staged photos in parent state. */
  onChange: (next: StagedPhoto[]) => void;
  /** Disable interactions during save. */
  disabled?: boolean;
  /** Field label override. Defaults to "Photos". */
  label?:    string;
  /** Helper text shown under the label. */
  hint?:     string;
}

function nextTempId(): string {
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DiaryPhotosField({ photos, onChange, disabled, label, hint }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map());
  // T-366 — single watermark toggle for the whole entry (default on). Applies
  // to both the camera and gallery paths; off = plain upload, no burn-in.
  const [watermarkOn, setWatermarkOn] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  function toggleWatermark(next: boolean) {
    setWatermarkOn(next);
    // Apply to everything already staged so the toggle governs the whole entry.
    onChange(photos.map(p => ({ ...p, watermark: next })));
  }

  // Manage object URLs — revoke when photos change or on unmount to avoid leak
  useEffect(() => {
    const urls = new Map<string, string>();
    for (const p of photos) {
      if (!previewUrls.has(p.tempId)) {
        urls.set(p.tempId, URL.createObjectURL(p.file));
      } else {
        urls.set(p.tempId, previewUrls.get(p.tempId)!);
      }
    }
    // Revoke any URLs for photos no longer in the list
    for (const [id, url] of previewUrls) {
      if (!urls.has(id)) URL.revokeObjectURL(url);
    }
    setPreviewUrls(urls);
    return () => {
      // On unmount, revoke all
      for (const url of urls.values()) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.map(p => p.tempId).join("|")]);

  function handleFilesPicked(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const incoming = Array.from(e.target.files ?? []);
    e.target.value = "";  // reset so picking the same file again triggers change

    if (incoming.length === 0) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setError(`Already at the ${MAX_PHOTOS}-photo cap. Remove one to add another.`);
      return;
    }

    const toAdd: StagedPhoto[] = [];
    const skipped: string[] = [];

    for (const f of incoming.slice(0, remaining)) {
      if (!ACCEPTED_MIMES.includes(f.type)) {
        skipped.push(`${f.name} (unsupported type ${f.type || "unknown"})`);
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        skipped.push(`${f.name} (${formatBytes(f.size)} > 10MB cap)`);
        continue;
      }
      toAdd.push({ tempId: nextTempId(), file: f, caption: "", kind: "general", watermark: watermarkOn });
    }

    if (incoming.length > remaining) {
      skipped.push(`${incoming.length - remaining} more — cap is ${MAX_PHOTOS} per entry`);
    }
    if (skipped.length > 0) setError(`Skipped: ${skipped.join("; ")}`);

    if (toAdd.length > 0) {
      onChange([...photos, ...toAdd]);
    }
  }

  function removePhoto(tempId: string) {
    onChange(photos.filter(p => p.tempId !== tempId));
  }

  function updateCaption(tempId: string, caption: string) {
    onChange(photos.map(p => p.tempId === tempId ? { ...p, caption } : p));
  }

  function updateKind(tempId: string, kind: string) {
    onChange(photos.map(p => p.tempId === tempId ? { ...p, kind } : p));
  }

  function move(tempId: string, dir: -1 | 1) {
    const idx = photos.findIndex(p => p.tempId === tempId);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= photos.length) return;
    const next = [...photos];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  }

  const totalBytes = photos.reduce((a, p) => a + p.file.size, 0);
  const atCap = photos.length >= MAX_PHOTOS;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
              {label ?? "Photos"}
              <span className="ml-2 text-text font-mono normal-case tracking-normal">
                {photos.length}/{MAX_PHOTOS}
              </span>
            </div>
            {hint && <div className="text-[12px] text-text-muted mt-0.5">{hint}</div>}
          </div>
          <div className="text-[11px] text-text-muted font-mono">
            {photos.length > 0 ? `total: ${formatBytes(totalBytes)}` : ""}
          </div>
        </div>
      </div>

      {/* T-366 — one watermark toggle for the whole entry, camera + gallery.
          On (default) burns date/GPS/site address; off uploads plain photos. */}
      <button
        type="button"
        onClick={() => toggleWatermark(!watermarkOn)}
        disabled={disabled}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md border text-[12px] transition-colors disabled:opacity-50 ${
          watermarkOn
            ? "bg-accent/5 border-accent/30 text-text"
            : "bg-ink-raised border-line text-text-muted"
        }`}
        title="Applies to both Take photo and Pick from gallery"
      >
        <Stamp size={15} className={watermarkOn ? "text-accent" : "text-text-muted"} />
        <span className="flex-1 text-left">
          Watermark photos
          <span className="block text-[11px] text-text-muted">
            {watermarkOn ? "Burning date, GPS & site address onto each photo." : "Off — uploading plain photos, no stamp."}
          </span>
        </span>
        <span className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${watermarkOn ? "bg-accent" : "bg-line"}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${watermarkOn ? "translate-x-4" : "translate-x-0.5"}`} />
        </span>
      </button>

      {/* Photo list */}
      {photos.length > 0 && (
        <div className="space-y-2">
          {photos.map((p, idx) => {
            const url = previewUrls.get(p.tempId);
            return (
              <div
                key={p.tempId}
                className="flex gap-3 p-2 rounded-md bg-ink-raised border border-line"
              >
                {/* Thumbnail */}
                <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-ink-card border border-line flex items-center justify-center">
                  {url ? (
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImagePlus size={20} className="text-text-muted" />
                  )}
                </div>
                {/* Details */}
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-text-muted">
                      #{idx + 1}
                    </span>
                    <span className="text-[12px] text-text truncate flex-1">{p.file.name}</span>
                    <span className="text-[10px] text-text-muted font-mono">
                      {formatBytes(p.file.size)}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <select
                      value={p.kind}
                      onChange={e => updateKind(p.tempId, e.target.value)}
                      disabled={disabled}
                      title="Category — groups the photo in the report"
                      className="w-[108px] flex-shrink-0 px-1.5 py-1 bg-ink-card border border-line rounded text-[11px] text-text outline-none focus:border-accent disabled:opacity-50"
                    >
                      {PHOTO_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="Caption (optional)"
                      value={p.caption}
                      onChange={e => updateCaption(p.tempId, e.target.value)}
                      disabled={disabled}
                      maxLength={200}
                      className="flex-1 min-w-0 px-2 py-1 bg-ink-card border border-line rounded text-[12px] text-text outline-none focus:border-accent disabled:opacity-50"
                    />
                  </div>
                </div>
                {/* Controls */}
                <div className="flex-shrink-0 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => move(p.tempId, -1)}
                    disabled={disabled || idx === 0}
                    className="p-1 rounded hover:bg-ink-hover disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp size={14} className="text-text-muted" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(p.tempId, 1)}
                    disabled={disabled || idx === photos.length - 1}
                    className="p-1 rounded hover:bg-ink-hover disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown size={14} className="text-text-muted" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePhoto(p.tempId)}
                    disabled={disabled}
                    className="p-1 rounded hover:bg-red-900/30 text-text-muted hover:text-red-300 disabled:opacity-50"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add buttons */}
      {!atCap && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={disabled}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-line hover:border-accent hover:bg-accent/5 text-[13px] text-text-muted hover:text-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera size={16} /> Take photo
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-line hover:border-accent hover:bg-accent/5 text-[13px] text-text-muted hover:text-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ImagePlus size={16} /> Pick from gallery
          </button>
        </div>
      )}
      {atCap && (
        <div className="text-[12px] text-text-muted text-center py-2">
          {MAX_PHOTOS}-photo cap reached. Remove one to add another.
        </div>
      )}

      {/* Camera capture input (single, opens camera on mobile) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFilesPicked}
        className="hidden"
      />
      {/* Gallery multi-select */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIMES.join(",")}
        multiple
        onChange={handleFilesPicked}
        className="hidden"
      />

      {error && (
        <div className="px-3 py-2 rounded bg-warn/10 border border-warn/30 text-[12px] text-warn flex items-start gap-2">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
